import { computed, provide, type ComputedRef } from 'vue'
import type { DeckCardInput } from '../../deck/types'
import {
  coerceDeckTemplateSettings,
  resolveDeckTemplateSettings,
  type DeckTemplateSettings,
} from '../../deck/templateSettings'
import {
  FLASHCARD_AUTOPLAY_AUDIO,
  FLASHCARD_DISABLE_AUTOPLAY,
  FLASHCARD_FORCE_AUDIO_VISIBLE,
  FLASHCARD_FORCE_HIDE_AUDIO,
  FLASHCARD_HIDE_AUDIO,
} from '../../flashcard/context'
import type { DeckPlayerModel, DeckPlayerShortcodeProps } from '../types'

type Props = {
  model: DeckPlayerModel
  deckTemplate: DeckTemplateSettings
  playerProps: DeckPlayerShortcodeProps
}

/** Back-compat: older saves used disableAutoplay=false to mean "autoplay audio". */
const applyLegacyAudioAutoplay = (t: DeckTemplateSettings | null): DeckTemplateSettings | null => {
  if (!t) return null
  // Not applied to embed props: WordPress often sends disableAutoplay:false by default.
  if (t.audioAutoplay === undefined && t.disableAutoplay === false)
    return { ...t, audioAutoplay: true }
  return t
}

/**
 * Decides whether card audio autoplays or is hidden and provides that to every
 * AudioElement below. In flowchart mode only play-audio nodes start audio.
 */
export function useAudioPolicy(
  props: Props,
  activeCard: ComputedRef<DeckCardInput | null>,
  userAutoplay: () => boolean,
) {
  const template = computed(() =>
    resolveDeckTemplateSettings({
      embed: coerceDeckTemplateSettings(props.playerProps as unknown),
      deck: applyLegacyAudioAutoplay(props.deckTemplate ?? null),
      card: applyLegacyAudioAutoplay(coerceDeckTemplateSettings(activeCard.value as unknown)),
    }),
  )

  const disableAutoplay = computed(
    () => props.model === 'flowchart' || template.value.disableAutoplay === true,
  )
  provide(FLASHCARD_DISABLE_AUTOPLAY, disableAutoplay)

  const autoplay = computed(() => {
    if (props.model === 'flowchart') return false
    if (disableAutoplay.value) return false
    if (template.value.audioAutoplay === true) return true
    if (template.value.audioNotVisible === true) return true // hidden audio implies autoplay
    return userAutoplay()
  })
  provide(FLASHCARD_AUTOPLAY_AUDIO, autoplay)

  // Hidden only while autoplaying (e.g. audioNotVisible plays silently in the background).
  const hideAudio = computed(() => {
    if (disableAutoplay.value) return false
    if (template.value.audioForceVisible === true) return false
    return template.value.audioNotVisible === true
  })
  provide(FLASHCARD_HIDE_AUDIO, hideAudio)

  // Hidden regardless of autoplay: the flowchart "hide audio" toggle.
  const forceHideAudio = computed(() => {
    if (template.value.audioForceVisible === true) return false
    return props.model === 'flowchart' && template.value.flowchartHideAudio === true
  })
  provide(FLASHCARD_FORCE_HIDE_AUDIO, forceHideAudio)

  provide(
    FLASHCARD_FORCE_AUDIO_VISIBLE,
    computed(() => template.value.audioForceVisible === true),
  )
}
